import { observable, IObservableArray } from "mobx";
import { copy, copyArray, mapArray, sort } from "../utils/data";
import { SkillFit, Profile, SavedOpportunity, ProfileSkill } from "./profile";
import { EmptyESId, StatusFlag, OperatorType, PrereqType, RefType, SkillLevel, PositionLevel, ApplicationProcess } from "./enums";

export interface IOpportunityResponse {
    opportunities: Array<OpportunitySummary>;
    deadOps: Array<string>;
}

export class OpportunitySummary {
    @observable name: string;
    @observable description: string;
    @observable location: string;
    @observable locIdHash: string;
    @observable allowApply: boolean;
    @observable idHash: string;
    @observable positionType: string;
    @observable templateId: string;
    @observable topSkills: Array<string>;
    @observable prereqs: Array<string>;
    @observable imageId: string;
    @observable coImageId: string;
    @observable category: string;
    @observable categoryId: string;
    @observable featured: boolean;
    @observable flagAsNew: boolean;
    @observable postedBy: string;
    @observable categoryColor: string;
    @observable titleColor: string;
    @observable positionCt: number;
    @observable keywords: Array<string>;
    @observable salaryRange: string;
    @observable expLevel: PositionLevel;
    @observable version: number;

    constructor(os?: OpportunitySummary) {
        this.name = this.description = this.location = this.locIdHash = this.idHash = this.positionType = this.templateId = this.categoryId
            = this.imageId = this.coImageId = this.category = this.postedBy = this.categoryColor = this.titleColor = this.salaryRange = "";
        this.allowApply = this.featured = this.flagAsNew = false;
        this.positionCt = this.version = 0;
        this.topSkills = new Array<string>();
        this.prereqs = new Array<string>();
        this.keywords = new Array<string>();
        this.expLevel = PositionLevel.NotSpecified;
        if (os) {
            copy(os, this, ["topSkills", "prereqs", "keywords"]);
            if (os.topSkills) {
                (this.topSkills as IObservableArray<string>).replace(os.topSkills);
            }
            if (os.prereqs) {
                (this.prereqs as IObservableArray<string>).replace(os.prereqs);
            }
            if (os.keywords) {
                (this.keywords as IObservableArray<string>).replace(os.keywords);
            }
        }
    }
}

export class CategoryOps {
    @observable categoryId: string;
    @observable opportunities: Array<OpportunitySummary>;

    constructor(catId: string) {
        this.categoryId = catId;
        this.opportunities = new Array<OpportunitySummary>();
    }

    updateOpportunities = (resp: IOpportunityResponse): void => {
        resp.deadOps.map((opId: string): void => {
            const summary: OpportunitySummary[] = this.opportunities.filter((os: OpportunitySummary): boolean => {
                return os.idHash === opId;
            });
            if (summary && summary.length > 0) {
                (this.opportunities as IObservableArray<OpportunitySummary>).remove(summary[0]);
            }
        });
        resp.opportunities.map((os: OpportunitySummary): void => {
            const summary: OpportunitySummary[] = this.opportunities.filter((existingOs: OpportunitySummary): boolean => {
                return existingOs.idHash === os.idHash && existingOs.version === os.version;
            });
            if (summary && summary.length > 0) {
                (this.opportunities as IObservableArray<OpportunitySummary>).remove(summary[0]);
            }
            this.opportunities.push(new OpportunitySummary(os));
        });
    }
}

export class SkillFactorMap {
    @observable factorId: string;
    @observable skill: string;

    constructor(sfm: SkillFactorMap) {
        copy(sfm, this);
    }
}

export class LevelMap {
    @observable from: string;
    @observable to: string;
    @observable alpha: boolean;
    @observable level: SkillLevel;

    constructor(lm?: LevelMap) {
        if (lm) {
            copy(lm, this);
        }
    }
}

export class CompanyLevels {
    @observable orgId: string;
    @observable default: boolean;
    @observable levels: Array<LevelMap>;

    constructor(cl?: CompanyLevels) {
        if (cl) {
            copy(cl, this, ["levels"]);
            copyArray(cl.levels, this.levels, (lm: LevelMap) => { return new LevelMap(lm); });
        }
    }
}

export class LearnedSkill {
    @observable skillId: string;
    @observable orgSkills: Array<CompanyLevels>;

    constructor(ls?: LearnedSkill) {
        this.skillId = EmptyESId;
        this.orgSkills = new Array<CompanyLevels>();
        if (ls) {
            this.skillId = ls.skillId;
            copyArray(ls.orgSkills, this.orgSkills, (cl: CompanyLevels) => { return new CompanyLevels(cl); });
        }
    }
}

export class CCLCReq {
    @observable title: string;
    @observable idHash: string;
    @observable status: StatusFlag;
    @observable description: string;
    @observable type: RefType;
    @observable fields: number;
    @observable reqFields: number;
    @observable skills: Array<LearnedSkill>;

    constructor(req?: CCLCReq) {
        this.title = "";
        this.idHash = EmptyESId;
        this.status = StatusFlag.Active;
        this.description = "";
        this.type = RefType.NotSpecified;
        this.fields = 0;
        this.reqFields = 0;
        this.skills = new Array<LearnedSkill>();
        if (req) {
            copy(req, this, ["skills"]);
            copyArray(req.skills, this.skills, (ls: LearnedSkill) => { return new LearnedSkill(ls); });
        }
    }
}

export class PrereqField {
    @observable opType: OperatorType;
    @observable type: PrereqType;
    @observable display: number;
    @observable refVal: any;
    @observable fieldMask: number;
    @observable reqFieldMask: number;
    @observable name: string;
    @observable refCclc: CCLCReq;

    constructor(pf: PrereqField) {
        copy(pf, this, ["refCclc"]);
        this.refCclc = new CCLCReq(pf.refCclc);
    }
}

export class Prereq {
    @observable fields: Array<PrereqField>;
    @observable title: string;
    @observable editable: false;
    @observable description: string;
    @observable mandatory: boolean;
    @observable global: boolean;
    @observable idHash: string;
    @observable status: StatusFlag;

    constructor(p: Prereq) {
        copy(p, this, ["fields"]);
        this.fields = new Array<PrereqField>();
        copyArray(p.fields, this.fields, (pf) => { return new PrereqField(pf); });
    }
}

export class SimFactor {
    @observable name: string;
    @observable progNorm: Array<number>;
    @observable progMult: Array<Array<number>>;
    @observable regNorm: Array<number>;
    @observable regMult: Array<Array<number>>;
    @observable idHash: string;

    constructor(sf: SimFactor) {
        this.name = sf.name;
        this.progNorm = new Array<number>();
        this.progMult = new Array<Array<number>>();
        this.regNorm = new Array<number>();
        this.regMult = new Array<Array<number>>();
        mapArray(sf.progNorm, this.progNorm);
        mapArray(sf.progMult, this.progMult);
        mapArray(sf.regNorm, this.regNorm);
        mapArray(sf.regMult, this.regMult);
        this.idHash = sf.idHash;
    }
}

export class SetSkill {
    @observable skillId: string;
    @observable level: number;
    @observable required: boolean;
    @observable title: string;
    @observable description: string;
    @observable use: string;
    @observable learn: string;
    @observable validate: string;
    @observable topSkill: boolean;
    @observable sharedTitle: string;
    @observable sharedDescription: string;
    @observable factorId: string;
    @observable mult: number;

    constructor(ss: SetSkill) {
        copy(ss, this);
    }
}

export class SkillSet {
    @observable name: string;
    @observable skills: Array<SetSkill>;

    constructor(ss: SkillSet) {
        this.name = ss.name;
        this.skills = new Array<SetSkill>();
        copyArray(ss.skills, this.skills, (s) => { return new SetSkill(s); });
    }
}

export class Opportunity {
    @observable name: string;
    @observable location: string;
    @observable locIdHash: string;
    @observable opens: string;
    @observable closes: string;
    @observable positionCt: number;
    @observable category: string;
    @observable categoryId: string;
    @observable categoryColor: string;
    @observable titleColor: string;
    @observable imageId: string;
    @observable coImageId: string;
    @observable postedBy: string;
    @observable briefDesc: string;
    @observable description: string;
    @observable templateId: string;
    @observable refTemplateId: string;
    @observable featured: boolean;
    @observable viewCt: number;
    @observable expLevel: number; 
    @observable salaryFrom: number;
    @observable salaryTo: number;
    @observable posTypeId: number; 
    @observable keywords: Array<string>;
    @observable allowApply: boolean;
    @observable flagAsNew: boolean;
    @observable applicationEmail: string;
    @observable appProcess: ApplicationProcess; 
    @observable extApplicationUrl: string;
    @observable factors: Array<SkillFactorMap>;
    @observable simFactors: Array<SimFactor>;
    @observable positionType: string;
    @observable orgIdHash: string;
    @observable skillSets: Array<SkillSet>;
    @observable mults: Array<any>; // do we use this?
    @observable prereqs: Array<Prereq>;
    @observable orgDisclaimer: string;
    @observable videoUrl: string;
    @observable applicationUrl: string;
    @observable idHash: string;
    @observable status: StatusFlag;

    constructor(op: Opportunity) {
        this.factors = new Array<SkillFactorMap>();
        this.simFactors = new Array<SimFactor>();
        this.skillSets = new Array<SkillSet>();
        this.prereqs = new Array<Prereq>();
        this.keywords = new Array<string>();
        copy(op, this, ["factors", "simFactors", "skillSets", "mults", "prereqs", "keywords"]);
        copyArray(op.factors, this.factors, (sfm) => { return new SkillFactorMap(sfm); });
        copyArray(op.simFactors, this.simFactors, (sf) => { return new SimFactor(sf); });
        copyArray(op.skillSets, this.skillSets, (ss) => { return new SkillSet(ss); });
        // mults
        copyArray(op.prereqs, this.prereqs, (p) => { return new Prereq(p); });
        mapArray(op.keywords, this.keywords);

    }
    hasSkill = (skillId: string): boolean => {
      let exists: boolean = false;
      this.skillSets.map((ss: SkillSet): void => {
        if (exists) { return; }
        ss.skills.map((sk: SetSkill): void => {
          if (sk.skillId === skillId) {
            exists = true;
          }
        });
      });
      return exists;
    }
    requiredSkills(): Array<SetSkill> {
        const skills: Array<SetSkill> = new Array<SetSkill>();
        this.skillSets.map((ss: SkillSet): void => {
            ss.skills.map((sk: SetSkill): void => {
                if (sk.required) {
                    skills.push(sk);
                }
            });
        });
        return sort(skills, "title");
    }
    desiredSkills(): Array<SetSkill> {
        const skills: Array<SetSkill> = new Array<SetSkill>();
        this.skillSets.map((ss: SkillSet): void => {
            ss.skills.map((sk: SetSkill): void => {
                if (!sk.required) {
                    skills.push(sk);
                }
            });
        });
        return sort(skills, "title");
    }
}

export class OpportunityFit {
    @observable opportunityId: string;
    @observable opportunityName: string;
    @observable fit: number;
    @observable status: number;
    @observable scored: string;
    @observable skillFit: Array<SkillFit>;
    @observable completed: boolean;
    //@observable prerequisites: Array<PrerequisiteFulfillment>;

    constructor(opfit: OpportunityFit) {
        this.skillFit = new Array<SkillFit>();
        copy(opfit, this, ["skillFit", "prerequisites"]);
        copyArray(opfit.skillFit, this.skillFit, (sf: SkillFit) => { return new SkillFit(sf); });
        //copyArray(opfit.prerequisites, this.prerequisites, (p: PrerequisiteFulfillment) => { return new PrerequisiteFulfillment(p); });
    }
}

export class ItemVersion {
    id: string;
    version: number;
    constructor(id: string, version: number) {
        this.id = id;
        this.version = version;
    }
}

export class ClientCache {
    existingOps: Array<ItemVersion>;

    constructor() {
        this.existingOps = new Array<ItemVersion>();
    }
    addItem = (id: string, version: number): void => {
        this.existingOps.push(new ItemVersion(id, version));
    }
}

export interface ISkillScore {
    skill: string;
    score: number;
}

export class SearchRequest {
  @observable name: string;
  @observable experienceLevels: Array<PositionLevel>;
  @observable companies: Array<string>;
  @observable locations: Array<string>;
  @observable categories: Array<string>;
  @observable minEdLevel: number;
  @observable onlyApplications: boolean;

  constructor() {
    this.name = "";
    this.experienceLevels = new Array<PositionLevel>();
    this.companies = new Array<string>();
    this.locations = new Array<string>();
    this.categories = new Array<string>();
    this.minEdLevel = 0;
    this.onlyApplications = false;
  }
}

export class RecommendedOpRequest {
  @observable savedOps: Array<string>;
  @observable skills: Array<string>;

  constructor(profile: Profile) {
    this.savedOps = new Array<string>();
    this.skills = new Array<string>();
    const tempOpIds: string[] = [];
    profile.savedOps.map((so: SavedOpportunity): void => {
      if ((so.fit & 1) > 0)
        tempOpIds.push(so.opportunityId);
    });
    (this.savedOps as IObservableArray<string>).replace(tempOpIds);
    const skillIds: string[] = [];
    profile.skills.map((ps: ProfileSkill): void => {
        skillIds.push(ps.idHash);
    });
    (this.skills as IObservableArray<string>).replace(skillIds);
  }
}


// WEBPACK FOOTER //
// ./src/models/opportunity.ts